import { FieldLabel, FlexBox, Switch } from '@dixa/lib-shared-habla';
import { PropsWithClassName } from 'lib-shared-types-utils';
import identity from 'lodash/identity';
import { ComponentPropsWithoutRef, ReactNode } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { HookFormBaseProps, useHookForm } from 'utils/hookForm';

type HookFormSwitchProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = HookFormBaseProps<
  TFieldValues,
  TName,
  Partial<
    Pick<ComponentPropsWithoutRef<typeof Switch>, 'disabled' | 'onChange'>
  >
> &
  PropsWithClassName & {
    /** The label. */
    label?: ReactNode;
    /**
     * The callback for transforming the value that is used when setting the
     * checked state of the switch.
     *
     * This is useful if the value is `false`, but the presentation requires
     * `true`.
     */
    transformValue?: (value: boolean) => boolean;
  };

/** Represents the React Hook Form variant of the {@link Switch} component. */
export const HookFormSwitch = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  transformValue = identity,
  label,
  className,
  ...fieldProps
}: HookFormSwitchProps<TFieldValues, TName>): JSX.Element => {
  const {
    controller: { field },
    controllerProps: { name: id },
  } = useHookForm(fieldProps);

  return (
    <FlexBox gap="200" alignItems="center" className={className}>
      {label && <FieldLabel htmlFor={id}>{label}</FieldLabel>}

      <Switch
        {...field}
        id={id}
        checked={transformValue(field.value)}
        disabled={fieldProps.componentProps?.disabled}
        onChange={(event) => {
          fieldProps.componentProps?.onChange?.(event);
          field.onChange(transformValue(event.target.checked));
        }}
      />
    </FlexBox>
  );
};
